import { Component, OnInit, Self } from '@angular/core';
import { FormBuilder, FormGroup, FormArray } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { DatePipe } from '@angular/common';
import { EquipmentManagenementService } from 'src/app/services/equipmentmanagement.service';
import { DefaultValuesService } from '../../../../../../services/defaultvalues.service';
import { DestroyService } from 'src/app/services/destroyservice';
import { takeUntil } from 'rxjs';
import { DialogService } from 'src/app/services/dialog.service';
import { ToastrService } from 'ngx-toastr';
import { EmtUpdateMandatoryPeripheralVersionResponseType } from "../../../../../../utils/utils";

interface TableRow {
  board: string;
  familyCode: string;
  version: string;
  releaseDate: Date;
  isMandatory: boolean;
  onEdit: boolean;
}

@Component({
  selector: 'mandatory-version-peripheral-list',
  templateUrl: './mandatory-version-peripheral-list.component.html',
  styleUrls: ['./mandatory-version-peripheral-list.component.less'],
  providers: [DatePipe, DestroyService],
  standalone: false
})

export class MandatoryVersionPeripheralListComponent implements OnInit {
  public form: FormGroup;
  public dataSource = new MatTableDataSource<TableRow>();
  public columnsToDisplay: string[] = ['board', 'familyCode', 'version', 'releaseDate', 'mandatory', 'actions'];
  private subscriptionStage: any;
  public currentControllerGroup: any;
  public originalPeripheralSoftwareVersions: PeripheralVersionsDto | undefined;

  constructor(private readonly formBuilder: FormBuilder,
    public datePipe: DatePipe,
    public defaults: DefaultValuesService,
    private readonly emtService: EquipmentManagenementService,
    private readonly dialog: DialogService,
    private readonly toastrService: ToastrService,
    @Self() private readonly destroy$: DestroyService) {
    this.form = this.formBuilder.group({
      rows: this.formBuilder.array([])
    });
  }

  ngOnInit(): void {
    this.subscriptionStage = this.defaults.stageMessageChanges$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.clearForm();
    });
  }

  get rows(): FormArray {
    return this.form.get('rows') as FormArray;
  }

  public clearForm() {
    this.form = this.formBuilder.group({
      rows: this.formBuilder.array([]),
    });
    this.form.reset();
    this.dataSource = new MatTableDataSource<any>([]);
  }

  loadPeripheralSoftwareVersions(groupId) {
    this.currentControllerGroup = groupId;
    this.emtService.getPeripheralSoftwareReleasesByCompatibilityGroup(groupId).subscribe({
      next: (data) => {
        this.originalPeripheralSoftwareVersions = data;
        this.setRows(this.originalPeripheralSoftwareVersions.versionList);
      },
      error: (err) => {
        console.error('Failed to load versions', err);
      }
    });
  }

  onCheckBoxChange(event: any, row: TableRow): void {
    const isChecked = event.checked;
    console.log(`Checkbox for ${row.board} is now ${isChecked ? 'checked' : 'unchecked'}`);
    row.isMandatory = isChecked;
  }

  mapToTableRows(items: PeripheralVersionItemDto[]): TableRow[] {
    return items.map(item => ({
      uniqueId: item.peripheralSoftwareGlobalControllerGroupId,
      board: item.functionTypeName,
      familyCode: item.familyCodeName,
      version: item.softwareVersion,
      releaseDate: new Date(item.releaseDateUtc),
      isMandatory: item.isMandatory,
      onEdit: false // default value
    }));
  }

  setRows(data: PeripheralVersionItemDto[]): void {
    const formGroups = data.map(row =>
      this.formBuilder.group({
        uniqueId: [row.peripheralSoftwareGlobalControllerGroupId],
        board: [row.functionTypeName],
        familyCode: [row.familyCodeName],
        version: [row.softwareVersion],
        releaseDate: [row.releaseDateUtc],
        isMandatory: [row.isMandatory],
        onEdit: false
      }));

    this.form.setControl('rows', this.formBuilder.array(formGroups));
    this.dataSource.data = this.mapToTableRows(data);
  }

  onRowEditInit(row: TableRow): void {
    const formIndex = this.findFormArrayIndex(row);
    if (formIndex === -1) return;
    const rows = this.form.get('rows') as FormArray;
    rows.at(formIndex).get('onEdit')?.patchValue(true);
    this.dataSource = new MatTableDataSource(rows.value);
  }

  onRowEditCancel(row: TableRow): void {
    const formIndex = this.findFormArrayIndex(row);
    if (formIndex === -1) return;
    this.updateFormValue(formIndex, 'onEdit', false);
    const rows = this.form.get('rows') as FormArray;
    this.dataSource = new MatTableDataSource(rows.value);
  }

  mapToEmtUpdateMandatoryPeripheralVersionDto(peripheralVersion: PeripheralVersionItemDto): EmtUpdateMandatoryPeripheralVersionItemDto {
    return new class implements EmtUpdateMandatoryPeripheralVersionItemDto {
      peripheralSoftwareVersionId = peripheralVersion.softwareVersionId;
      peripheralSoftwareVersionName = peripheralVersion.softwareVersion;
      globalControllerGroupCompatibilityId = peripheralVersion.peripheralSoftwareGlobalControllerGroupId;
      isMandatory = peripheralVersion.isMandatory;
    };
  }

onSave(): void {
  if (!this.hasChanges()) return;

  const modified = this.form.value.rows.filter(x => x.onEdit);

  const updateMandatoryList = modified
    .map(element => {
      const peripheralSoftware = this.originalPeripheralSoftwareVersions?.versionList.find(
        y => y.peripheralSoftwareGlobalControllerGroupId === element.uniqueId
      );

      if (peripheralSoftware && peripheralSoftware.isMandatory !== element.isMandatory) {
        peripheralSoftware.isMandatory = element.isMandatory;
        return this.mapToEmtUpdateMandatoryPeripheralVersionDto(peripheralSoftware);
      }

      return null;
    })
    .filter(dto => dto !== null);

  if (updateMandatoryList.length === 0) return;

  const requestBody: EmtUpdateMandatoryPeripheralVersionsDto = {
    updateMandatoryList
  };

  this.emtService.updatePeripheralSoftwareVersionsAsMandatory(requestBody).subscribe({
    next: (responseList: EmtUpdateMandatoryPeripheralVersionResponseViewModel[]) => {
      const hasOnlySuccess = responseList.every(
        r => r.responseType === EmtUpdateMandatoryPeripheralVersionResponseType.Success
      );

      if (hasOnlySuccess) {
        this.toastrService.success('Your changes have been saved.');
      } else {
        const errorResponses = responseList.filter(
          r => r.responseType !== EmtUpdateMandatoryPeripheralVersionResponseType.Success
        );

        const errorMessages = errorResponses.map(r => {
          const match = this.originalPeripheralSoftwareVersions?.versionList.find(
            x => x.peripheralSoftwareGlobalControllerGroupId === r.componentSoftwareReleaseGlobalControllerGroupCompatibilityId
          );

          const functionTypeName = match?.functionTypeName ?? 'Unknown';
          return `${functionTypeName}: ${r.message}`;
        });

        this.dialog.showRegularErrorDialog(errorMessages.join('\n'));
      }

      this.loadPeripheralSoftwareVersions(this.currentControllerGroup);
    },
    error: (err) => {
      this.dialog.showRegularErrorDialog(err.error);
      this.loadPeripheralSoftwareVersions(this.currentControllerGroup);
      console.error(err);
    }
  });
}

  private updateFormValue(index: number, fieldName: string, newValue: any) {
    const rows = this.form.get('rows') as FormArray;
    const row = rows.at(index);
    row.get(fieldName)?.setValue(newValue);
  }

  private findFormArrayIndex(row: any): number {
    const rows = this.form.get('rows') as FormArray;
    return rows.controls.findIndex(control => control.value.uniqueId === row.uniqueId);
  }

  hasChanges(): boolean {
    return this.form.value.rows.some(x => x.onEdit);
  }
}